// ControlProductoActividad.js
// Este componente maneja la lista de control producto por actividad,incluye edicion de Grupos y Detalles de actividades
// Grupo y Detalle se manejan de manera separadas

import {
  AuditOutlined,
  DeleteOutlined,
  EditOutlined,
  FileProtectOutlined,
  PercentageOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Tooltip,
  message
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import SelectableListModalCoberturas from '../../components/SelectableListModalCoberturas';
import { CONFIG } from '../../constants';
import {
  getCoberturaLabel,
  getCostoLabel
} from '../../functions/opcionesSubcobertura';
import { errorHandler, getDescripcionOpcion } from '../../utils';
import { instanceRef } from '../../utils/axios';
import { formatoMonedaGenerico } from '../../utils/converters';
import ControlProductoModal from './ControlProductoModal';

const { Option } = Select;

/**
 * ControlProductoActividad Componente.
 *
 * @component
 * @param {Object} props.autorizacionConfig - Config de autorizacion.
 * @param {boolean} props.loading - Loading estado.
 * @param {Function} props.setLoading - Funcion para actualizar el estado del loading
 * @param {number} props.idAseguradora - Id de Aseguradora
 * @param {number} props.idProducto - Id de Producto
 */
const ControlProductoActividad = ({
  autorizacionConfig,
  loading,
  setLoading,
  upsertAutorizacionConfig,
  idAseguradora,
  idProducto
}) => {
  const [modalControlOpen, setModalControlOpen] = useState(false);
  const [modalGrupoOpen, setModalGrupoOpen] = useState(false);
  const [modalDetalleOpen, setModalDetalleOpen] = useState(false);
  const [modalAutorizacionOpen, setModalAutorizacionOpen] = useState(false);
  const [actividades, setActividades] = useState([]);
  const [actividadDetalles, setActividadDetalles] = useState([]);
  const [, setControlProductoCoberturas] = useState([]);

  const [initialValues, setInitialValues] = useState(null);
  const [autorizar, setAutorizar] = useState();
  const [modalRecargoOpen, setModalRecargoOpen] = useState();
  const [modalDeleteOpen, setModalDeleteOpen] = useState();

  const [data, setData] = useState([]);

  const [form] = Form.useForm();

  // Columns para coberturas TODO ver para definir en algún archivo común ya que se usa en otra parte también
  const columnsCobertura = [
    {
      title: 'Subcobertura',
      render: (text, r) => {
        const coberturaObj = getCoberturaLabel(r);
        return `
            ${r.cobertura.descripcion} | Tipo: ${getDescripcionOpcion(
          r.opcion
        )} - Cobertura: ${coberturaObj ? coberturaObj.label : ''}
        ${getCostoLabel(r) ? ` - Costo: ${getCostoLabel(r)}` : ''}
        ${
          r.costo_min
            ? ` - Costo Mínimo: ${formatoMonedaGenerico(r.moneda, r.costo_min)}`
            : ''
        }
        ${
          r.costo_max
            ? ` - Costo Máximo: ${formatoMonedaGenerico(r.moneda, r.costo_max)}`
            : ''
        }
        - ${r.moneda.simbolo}
      `;
      }
    }
  ];

  // Config de columnas para la tabla de antd
  const columns = [
    {
      title: 'Tipo de Actividad',
      key: 'actividad',
      align: 'right',
      render: (_, record) => {
        const { actividad, detalle, recargo } = record;
        const bloquearBotonEditar = detalle && recargo;
        return (
          <>
            {actividad ? actividad.grupo : ''}{' '}
            <Tooltip
              title={
                !bloquearBotonEditar
                  ? 'Editar'
                  : 'Desactive Recargos y/o Coberturas para realizar combinaciones.'
              }
            >
              <Button
                type="text"
                icon={<EditOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
                size="small"
                onClick={() => handleEditGrupoClick(record)}
                disabled={bloquearBotonEditar}
              />
            </Tooltip>
          </>
        );
      }
    },
    {
      title: 'Rubro',
      key: 'detalle',
      align: 'right',
      render: (record) => {
        const { detalle, actividad, recargo } = record;
        const bloquearBotonEditar = actividad && recargo;

        return (
          <>
            {detalle ? detalle.detalle : ''}{' '}
            <Tooltip
              title={
                !bloquearBotonEditar
                  ? 'Editar'
                  : 'Desactive Recargos y/o Coberturas para realizar combinaciones.'
              }
            >
              <Button
                type="text"
                icon={<EditOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
                size="small"
                onClick={() => handleEditDetalleClick(record)}
                disabled={bloquearBotonEditar}
              />
            </Tooltip>
          </>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: '10px',
      align: 'left',
      render: (record) => {
        const controlProducto = record;
        const {
          autorizacion,
          recargo,
          cPC,
          id_actividad,
          id_actividad_detalle
        } = controlProducto;
        const tieneCoberturas =
          cPC?.length > 0 ? cPC.some((item) => item.subcobertura) : false;
        const bloquearBotonRecargo =
          id_actividad && id_actividad_detalle && !recargo;
        const bloqueoBotonCoberturas =
          id_actividad && id_actividad_detalle && !cPC?.length > 0;
        const tituloCoberturas = !bloqueoBotonCoberturas
          ? 'Opciones de Coberturas'
          : 'Desactive Actividad o Rubro para elegir Coberturas.';
        return (
          <Space size="small">
            <Tooltip title="Autorización">
              <Button
                type="text"
                icon={
                  <AuditOutlined
                    style={{
                      color: autorizacion
                        ? CONFIG.COLOR_PRIMARY
                        : 'rgba(0,0,0,.45)'
                    }}
                    size="middle"
                  />
                }
                size="small"
                onClick={() => handleClickAutorizacion(record)}
                style={{ color: 'rgba(0,0,0,.45)' }}
              />
            </Tooltip>

            <Tooltip
              title={
                !bloquearBotonRecargo
                  ? 'Recargo'
                  : 'Desactive Actividad o Rubro para aplicar Recargo.'
              }
            >
              <Button
                type="text"
                icon={
                  <PercentageOutlined
                    style={{
                      color: recargo ? CONFIG.COLOR_PRIMARY : 'rgba(0,0,0,.45)'
                    }}
                  />
                }
                size="small"
                onClick={() => handleClickRecargo(record)}
                style={{ color: 'rgba(0,0,0,.45)' }}
                disabled={bloquearBotonRecargo}
              />
            </Tooltip>
            {idAseguradora && idProducto && (
              <SelectableListModalCoberturas
                title={tituloCoberturas}
                iconButton={
                  <UnorderedListOutlined
                    style={{
                      color: tieneCoberturas
                        ? CONFIG.COLOR_PRIMARY
                        : 'rgba(0,0,0,.45)'
                    }}
                  />
                }
                controlProductoCoberturas={cPC}
                setControlProductoCoberturas={setControlProductoCoberturas}
                isMultiple
                columnsData={columnsCobertura}
                url={`/cobertura/search/subcobertura?idAseguradora=${idAseguradora}&idProducto=${idProducto}`}
                width={1200}
                idAseguradora={idAseguradora}
                idProducto={idProducto}
                data={record}
                handleChange={handleCoberturaOk}
                size="small"
                disabledButton={bloqueoBotonCoberturas}
              />
            )}
            <Tooltip title="Eliminar">
              {' '}
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => handleClickDeleteActividad(record)}
                style={{ color: 'rgba(0,0,0,.45)' }}
              />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  // handleButtonClick: Abre el modal de control producto
  const handleButtonClick = () => {
    setModalControlOpen(true);
  };

  const handleClickAutorizacion = (controlProducto) => {
    setInitialValues({ ...controlProducto });
    form.setFieldsValue({ ...controlProducto });
    setAutorizar(controlProducto.autorizacion);
    setModalAutorizacionOpen(true);
  };
  const handleClickRecargo = (itemControlProducto) => {
    setInitialValues({ ...itemControlProducto });
    form.setFieldsValue({ ...itemControlProducto });
    setModalRecargoOpen(true);
  };

  const handleEditGrupoClick = (controlProducto) => {
    setInitialValues({ ...controlProducto });
    form.setFieldsValue({ ...controlProducto });
    setModalGrupoOpen(true);
  };

  const handleEditDetalleClick = (controlProducto) => {
    setInitialValues({ ...controlProducto });
    form.setFieldsValue({ ...controlProducto });
    setModalDetalleOpen(true);
  };

  const handleClickDeleteActividad = (itemControlProducto) => {
    setInitialValues({ ...itemControlProducto });
    form.setFieldsValue({ ...itemControlProducto });
    setModalDeleteOpen(true);
  };

  // handleAddRowActividad: Agrega una fila de control producto
  const handleAddRowActividad = () => {
    const newId = Number(data.length + 1); // Generate a new id for the new row
    const newDataRow = {
      key: newId,
      row: 0, // Set the row value to 0 for the new row
      id: null,
      id_actividad: null,
      id_actividad_detalle: null,
      actividad: null,
      detalle: null,
      id_autorizacion_config: autorizacionConfig.id
    };

    const newData = [newDataRow, ...data]; // Agrega la nueva fila al principio de la lista
    setData(newData);

    // Actualiza el valor del row del resto de las filas.
    const updatedData = newData.map((item, index) => ({
      ...item,
      row: index
    }));

    setData(updatedData);

    setModalDetalleOpen(false); // Close the modal
  };
  const handleDeleteRowActividad = () => {
    // Identificamos el indice del objeto que se quiere eliminar
    const indexToDelete = data.findIndex(
      (item) => item.id === initialValues.id
    );
    // agregamos el campo eliminado al objeto a eliminar
    if (indexToDelete !== -1) {
      data[indexToDelete].eliminado = true;
    }
    const newData = [...data];
    setData(newData);

    setModalDeleteOpen(false); // Close the modal
  };

  // handleGrupoOk: Seteo de datos del formulario de la Edicion de Grupo
  const handleGrupoOk = () => {
    const valuesForm = form.getFieldsValue();
    const actividadId = valuesForm.actividad.id;
    const newActividad = actividades.find((item) => item.id === actividadId);
    const newData = data.map((item) => {
      if (
        (item.id && item.id === initialValues.id) ||
        (!item.id && item.row === initialValues.row)
      ) {
        const newControlProducto = {
          ...item,
          id_actividad: newActividad.id,
          actividad: newActividad,
          editado: true
        };
        return newControlProducto;
      }

      return item;
    });
    setData(newData);
    form.resetFields();
    setModalGrupoOpen(false);
  };

  // handleDetalleOk: Seteo de datos del formulario de la Edicion de Detalle
  const handleDetalleOk = () => {
    const valuesForm = form.getFieldsValue();
    const detalleId = valuesForm.detalle.id;
    const newDetalle = actividadDetalles.find((item) => item.id === detalleId);

    const newData = data.map((item) => {
      if (
        (item.id && item.id === initialValues.id) ||
        (!item.id && item.row === initialValues.row)
      ) {
        const newControlProducto = {
          ...item,
          id_actividad_detalle: newDetalle.id,
          detalle: newDetalle,
          editado: true
        };
        return newControlProducto;
      }

      return item;
    });
    setData(newData);
    form.resetFields();
    setModalDetalleOpen(false);
  };

  // handleAutorizacionOk: Seteo de datos del formulario de Autorizacion
  const handleAutorizacionOk = () => {
    const newData = data.map((item) => {
      if (
        (item.id && item.id === initialValues.id) ||
        (!item.id && item.row === initialValues.row)
      ) {
        const newControlProducto = {
          ...item,
          autorizacion: autorizar,
          editado: true
        };
        return newControlProducto;
      }

      return item;
    });
    setData(newData);
    form.resetFields();
    setModalAutorizacionOpen(false);
  };
  //  initialValue es el item que se esta editando
  const handleRecargoOk = () => {
    // Si el valor editado ya existe en data, se actualiza el valor
    const newData = data.map((item) => {
      if (
        (item.id && item.id === initialValues.id) ||
        (!item.id && item.row === initialValues.row)
      ) {
        const newControlProducto = {
          ...item,
          recargo: form.getFieldValue('recargo'),
          editado: true
        };
        return newControlProducto;
      }
      // Si no existe se agrega como item nuevo
      return item;
    });
    setData(newData);
    form.resetFields();
    setModalRecargoOpen(false);
  };

  const handleCoberturaOk = (updatedCoberturasList, controlProducto) => {
    const newData = data.map((item) => {
      if (
        (item.id && item.id === controlProducto.id) ||
        (!item.id && item.row === controlProducto.row)
      ) {
        const newControlProducto = {
          ...item,
          cPC: updatedCoberturasList,
          editado: true
        };
        return newControlProducto;
      }

      return item;
    });
    setData(newData);
  };

  const handleGrupoCancel = () => {
    form.resetFields();
    setModalGrupoOpen(false);
  };

  const handleDetalleCancel = () => {
    setModalDetalleOpen(false);
  };

  const handleAutorizacionCancel = () => {
    setModalAutorizacionOpen(false);
  };
  const handleRecargoCancel = () => {
    setModalRecargoOpen(false);
  };
  const handleDeleteCancel = () => {
    setModalDeleteOpen(false);
  };

  const validarControles = (controlProductoActividadList) => {
    let validacionOk = true;
    for (let i = 0; i < controlProductoActividadList.length; i += 1) {
      const control = controlProductoActividadList[i];
      if (
        control.id_actividad &&
        control.id_actividad_detalle &&
        (control.recargo || control.cPC?.length > 0)
      ) {
        validacionOk = false;
        break;
      }
    }
    return validacionOk;
  };

  // handleConfirm:  Se llama a la funcion update para Actualizacion de datos de la lista de control producto
  const handleConfirm = async () => {
    // TODO: validaciones obligatorio al menos uno autorizacion/recargo/cobertura.
    // Verificar si al menos un dato ha sido agregado, editado.
    const controlProductoActividadList = [...data];
    const controlesOk = validarControles(controlProductoActividadList);
    if (!controlesOk) {
      return message.warning(
        'Existen combinaciones de Actividad y Rubro que tienen Recargo o Coberturas'
      );
    }

    setModalControlOpen(false);
    const modifiedConfig = {
      ...autorizacionConfig,
      editado: true,
      cP: controlProductoActividadList
    }; // cP controlProducto
    upsertAutorizacionConfig([modifiedConfig]);
    return null;
  };

  const getActividades = useCallback(async () => {
    try {
      const actividadesRes = await instanceRef.instance.get(
        `/controlproductoactividad/all/actividad`
      );
      if (actividadesRes.data?.data) {
        setActividades(actividadesRes.data?.data);
      }
      return actividadesRes;
    } catch (error) {
      errorHandler(error);
      return null;
    }
  }, []);

  const getActividadDetalles = useCallback(async () => {
    try {
      const actividadesRes = await instanceRef.instance.get(
        `/controlproductoactividad/all/actividaddetalle`
      );
      if (actividadesRes.data?.data) {
        setActividadDetalles(actividadesRes.data?.data);
      }
      return actividadesRes;
    } catch (error) {
      errorHandler(error);
      return null;
    }
  }, []);

  const getControlProductos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await instanceRef.instance.get(
        `/controlproductoactividad/all/${autorizacionConfig.id}`
      );
      if (response.data?.data) {
        setData(response.data?.data);
      }
      setLoading(false);
      return response;
    } catch (error) {
      setLoading(false);
      errorHandler(error);
      return null;
    }
  }, [autorizacionConfig, setLoading]);

  const FormGrupo = () => {
    return (
      <Form
        name="grupo"
        initialValues={initialValues}
        wrapperCol={{ span: 16 }}
        form={form}
      >
        <Col xs={24} md={24} align="start">
          <Form.Item
            label="Grupo"
            name={['actividad', 'id']}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: '100%' }}
              optionFilterProp="children"
              placeholder="Seleccionar un grupo"
            >
              {actividades.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.grupo}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Form>
    );
  };

  const FormDetalle = () => {
    return (
      <Form
        name="detalle"
        initialValues={initialValues}
        wrapperCol={{ span: 16 }}
        form={form}
      >
        <Col xs={24} md={24} align="start">
          <Form.Item
            label="Detalle"
            name={['detalle', 'id']}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: '100%' }}
              optionFilterProp="children"
              placeholder="Seleccionar un detalle"
            >
              {actividadDetalles.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.detalle}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Form>
    );
  };

  const FormAutorizacion = () => {
    return (
      <Form
        name="autorizacion"
        initialValues={initialValues}
        wrapperCol={{ span: 16 }}
        form={form}
      >
        <Col xs={24} md={24} align="start">
          <Form.Item
            label="¿Desea que se realice una Autorización?"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Si"
              unCheckedChildren="No"
              checked={autorizar}
              onChange={(value) => setAutorizar(!!value)}
            />
          </Form.Item>
        </Col>
      </Form>
    );
  };
  const FormRecargo = () => {
    return (
      <Form
        name="recargo"
        initialValues={initialValues}
        wrapperCol={{ span: 16 }}
        form={form}
      >
        <Col xs={24} md={24} align="start">
          <Form.Item
            label="Porcentaje de Recargo"
            name="recargo"
            normalize={(value) => {
              if (Number(value) > 100) {
                return 100;
              }
              if (Number(value) < 0) {
                return 0;
              }
              return Number(value);
            }}
          >
            <Input
              min={0}
              max={100}
              step={0.1}
              placeholder="0"
              type="number"
              suffix="%"
              style={{ width: 100, textAlign: 'end' }}
            />
          </Form.Item>
        </Col>
      </Form>
    );
  };

  useEffect(() => {
    // Fetch data or perform any other initialization logic
    getActividades();
    getActividadDetalles();
    getControlProductos();
  }, [getActividades, getActividadDetalles, getControlProductos]);

  return (
    <>
      <Tooltip title="Control Producto">
        <Button
          icon={<FileProtectOutlined />}
          type="text"
          size="middle"
          onClick={handleButtonClick}
        />
      </Tooltip>

      <ControlProductoModal
        open={modalControlOpen}
        setOpen={setModalControlOpen}
        columns={columns}
        dataSourceTable={data.filter((item) => !item.eliminado)}
        handleAddRow={handleAddRowActividad}
        handleConfirm={handleConfirm}
        loading={loading}
        setLoading={setLoading}
      />

      <Modal
        key="grupo"
        title="Editar Grupo"
        open={modalGrupoOpen}
        maskClosable={false}
        footer={[
          <Button key="grupoCancel" onClick={() => handleGrupoCancel()}>
            Cancelar
          </Button>,
          <Button key="grupoOk" type="primary" onClick={() => handleGrupoOk()}>
            Aceptar
          </Button>
        ]}
      >
        <FormGrupo />
      </Modal>
      <Modal
        key="detalle"
        title="Editar Detalle"
        open={modalDetalleOpen}
        maskClosable={false}
        footer={[
          <Button key="detalleCancel" onClick={() => handleDetalleCancel()}>
            Cancelar
          </Button>,
          <Button
            key="detalleOk"
            type="primary"
            onClick={() => handleDetalleOk()}
          >
            Aceptar
          </Button>
        ]}
      >
        <FormDetalle />
      </Modal>
      <Modal
        key="autorizacion"
        title="Autorización"
        open={modalAutorizacionOpen}
        maskClosable={false}
        footer={[
          <Button key="cancelAut" onClick={() => handleAutorizacionCancel()}>
            Cancelar
          </Button>,
          <Button
            key="okAut"
            type="primary"
            onClick={() => handleAutorizacionOk()}
          >
            Aceptar
          </Button>
        ]}
      >
        <FormAutorizacion />
      </Modal>
      <Modal
        key="recargo"
        title="Recargo"
        open={modalRecargoOpen}
        maskClosable={false}
        footer={[
          <Button key="cancelAut" onClick={() => handleRecargoCancel()}>
            Cancelar
          </Button>,
          <Button key="okAut" type="primary" onClick={() => handleRecargoOk()}>
            Aceptar
          </Button>
        ]}
      >
        <FormRecargo />
      </Modal>
      <Modal
        key="deleteActividad"
        title="Eliminar"
        open={modalDeleteOpen}
        maskClosable={false}
        footer={[
          <Button key="cancelAut" onClick={() => handleDeleteCancel()}>
            Cancelar
          </Button>,
          <Button
            key="okAut"
            type="primary"
            onClick={() => handleDeleteRowActividad()}
          >
            Aceptar
          </Button>
        ]}
      >
        ¿Está seguro? Se eliminará toda la información del Control Producto
        Actividad
      </Modal>
    </>
  );
};

export default ControlProductoActividad;
